<template>
    <div>
        <InertiaHead :title="title ? `${title} - ${$page.props.appName}` : $page.props.appName">
            <meta v-if="metaDescription" head-key="description" name="description" :content="metaDescription" />
            <slot name="head"/>
        </InertiaHead>
        <div class="min-h-screen flex flex-col bg-gray-100">
            <div class="h-9 bg-gray-900">
                <div class="max-w-7xl mx-auto px-3 flex justify-between items-center h-full">
                    <div class="flex items-center text-gray-200">
                        <SvgIcon class="mr-1" :size="18" name="email"/>
                        <span>{{$page.props.siteData.email}}</span>
                    </div>
                    <div class="sm:flex hidden">
                        <InertiaLink
                            v-for="(lang, key) in languages" :key="key"
                            method="get"
                            :href="route('lang.switch', key)"
                            :class="{
                                ['opacity-100'] : key == locale,
                                ['opacity-50 filter grayscale hover:grayscale-0 hover:opacity-90 transition duration-300'] : key != locale,
                            }"
                            class="w-6 h-6 mx-1 "
                        >
                            <img class="w-6 h-6 max-w-max" width="24" height="24" :src="getLangImage(key)">
                        </InertiaLink>
                    </div>
                    <BreezeDropdown
                        class="sm:hidden block"
                        align="right"
                        width="48"
                    >
                        <template #trigger>
                            <span class="inline-flex items-center rounded-md text-gray-200">
                                <img class="mx-1 h-6 w-6 max-w-max" width="24" height="24" :src="getLangImage(locale)">
                                <svg class="-mr-0.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </span>
                        </template>
                        <template
                            #content
                        >
                        <div v-for="(lang, key) in languages" :key="key">
                            <BreezeDropdownLink
                                v-if="key != locale"
                                :href="route('lang.switch', key)"
                                class="flex justify-between"
                            >
                                {{ __(lang) }}
                                <img class="mx-1 w-6 h-6 max-w-max" :src="getLangImage(key)">
                            </BreezeDropdownLink>
                        </div>
                        </template>
                    </BreezeDropdown>
                </div>
            </div>
            <nav class="bg-white border-b border-gray-100 sticky top-0 z-30 md:px-10 sm:px-6 px-3"
                :class="$slots.header ? '' : 'shadow'"
            >
                <!-- Primary Navigation Menu -->
                <div class="max-w-7xl mx-auto">
                    <div class="flex justify-between h-16">
                        <div class="flex">
                            <!-- Logo -->
                            <div class="flex-shrink-0 flex items-center">
                                <InertiaLink :href="route('home')">
                                    <BreezeApplicationLogo class="block h-12 w-auto text-gray-800" />
                                </InertiaLink>
                            </div>

                            <!-- Navigation Links -->
                            <div class="hidden space-x-2 sm:-mb-px sm:ml-10 sm:flex navbar-links">
                                <BreezeNavLink :href="route('page.show', 'about-us')" :active="route().current('page.show', 'about-us')">
                                    {{__('About')}}
                                </BreezeNavLink>
                                <BreezeNavLink :href="route('gallery')" :active="route().current('gallery')">
                                    {{__('Gallery')}}
                                </BreezeNavLink>
                                <BreezeNavLink :href="route('contact')" :active="route().current('contact')">
                                    {{__('Contact')}}
                                </BreezeNavLink>
                            </div>
                        </div>

                        <div class="flex">
                            <div class="relative my-auto group">
                                <IconLink
                                    icon="cart"
                                    class="p-1"
                                    color="gray"
                                    :href="route('cart')"
                                />
                                <div
                                    v-if="$page.props.cartitems && $page.props.cartitems.length"
                                    style="font-size: .6em;"
                                    class="pointer-events-none group-hover:bg-white group-hover:border-gray-900 transition border-2 border-white group-hover:text-black absolute bg-green-500 rounded-full px-1.5 py-1 bottom-0 right-0 text-white leading-none font-bold"
                                >
                                    {{ $page.props.cartitems.length }}
                                </div>
                            </div>
                            <div class="hidden sm:flex sm:items-center">
                                <!-- Settings Dropdown -->
                                <div class="ml-3 relative">
                                    <BreezeDropdown align="right" width="64">
                                        <template #trigger>
                                            <span class="inline-flex rounded-md">
                                                <button type="button" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md bg-gray-900 text-gray-200 hover:bg-gray-700 focus:outline-none transition ease-in-out duration-150">
                                                    {{ $page.props.loggedin ? $page.props.auth.user.first_name : __('Log in') }}

                                                    <svg class="ml-2 -mr-0.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                                    </svg>
                                                </button>
                                            </span>
                                        </template>

                                        <template
                                            #content
                                            v-if="$page.props.loggedin"
                                        >
                                            <BreezeDropdownLink :href="route('dashboard.admin.products')"  v-if="$page.props.admin">
                                                {{ __('Admin panel') }}
                                            </BreezeDropdownLink>
                                            <BreezeDropdownLink :href="route('dashboard.main')" >
                                                {{ __('Dashboard') }}
                                            </BreezeDropdownLink>
                                            <BreezeDropdownLink :href="route('logout')" method="post" as="button">
                                                {{ __('Log out') }}
                                            </BreezeDropdownLink>

                                        </template>
                                        <template
                                            #content
                                            v-else
                                        >
                                            <form @submit.prevent="login()" class="px-3 pt-2 border-b mb-2 pb-2">
                                                <div class="grid grid-cols-1 gap-3">
                                                    <InputLabel
                                                        id="email"
                                                        type="email"
                                                        :label="__('Email')"
                                                        autocomplete="email"
                                                        v-model="login_form.email"
                                                    />
                                                    <InputLabel
                                                        id="password"
                                                        type="password"
                                                        :label="__('Password')"
                                                        autocomplete="current-password"
                                                        v-model="login_form.password"
                                                    />
                                                </div>

                                                <div class="block mt-4">
                                                    <label class="flex items-center">
                                                        <breeze-checkbox name="remember" v-model:checked="login_form.remember" />
                                                        <span class="ml-2 text-sm text-gray-600">{{__('Remember me')}}</span>
                                                    </label>
                                                </div>

                                                <div class="mt-4">
                                                        <Button
                                                            :class="{
                                                                'opacity-25': login_form.processing
                                                            }"
                                                            @click="open = false"
                                                            :disabled="login_form.processing"
                                                            padding="md"
                                                            class="w-full mb-2 justify-center"
                                                        >
                                                            {{ __('Log in') }}
                                                        </Button>

                                                        <InertiaLink
                                                            :href="route('password.request')"
                                                            class="underline text-sm text-gray-600 hover:text-gray-900">
                                                            {{__('Forgot your password?')}}
                                                        </InertiaLink>
                                                </div>
                                            </form>
                                            <BreezeDropdownLink :href="route('register')">
                                                {{ __('Register') }}
                                            </BreezeDropdownLink>
                                        </template>
                                    </BreezeDropdown>
                                </div>
                            </div>

                            <!-- Hamburger -->
                            <div class="-mr-2 flex items-center sm:hidden">
                                <button @click="showingNavigationDropdown = ! showingNavigationDropdown" class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition duration-150 ease-in-out">
                                    <svg class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                        <path :class="{'hidden': showingNavigationDropdown, 'inline-flex': ! showingNavigationDropdown }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                                        <path :class="{'hidden': ! showingNavigationDropdown, 'inline-flex': showingNavigationDropdown }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Responsive Navigation Menu -->
                <div :class="{'block': showingNavigationDropdown, 'hidden': ! showingNavigationDropdown}" class="sm:hidden">
                    <div class="pt-2 pb-3 space-y-1">
                        <BreezeResponsiveNavLink :href="route('page.show', 'about-us')" :active="route().current('page.show', 'about-us')">
                            {{__('About')}}
                        </BreezeResponsiveNavLink>
                        <BreezeResponsiveNavLink :href="route('gallery')" :active="route().current('gallery')">
                            {{__('Gallery')}}
                        </BreezeResponsiveNavLink>
                        <BreezeResponsiveNavLink :href="route('contact')" :active="route().current('contact')">
                            {{__('Contact')}}
                        </BreezeResponsiveNavLink>
                    </div>

                    <!-- Responsive Settings Options -->
                    <div class="pt-4 pb-1 border-t border-gray-200" v-if="$page.props.auth.user && Object.keys($page.props.auth.user).length !== 0">
                        <div class="px-4 py-2 bg-gray-50">
                            <div class="font-medium text-base text-gray-800 font-bold">{{ getFullName($page.props.auth.user) }}</div>
                            <div class="font-medium text-sm text-gray-500">{{ $page.props.auth.user.email }}</div>
                        </div>

                        <div class="mt-3 space-y-1">
                            <BreezeResponsiveNavLink
                                :href="route('dashboard.admin.products')"
                                :active="route().current('dashboard.admin.products')"
                                v-if="$page.props.admin"
                            >
                                {{ __('Admin panel') }}
                            </BreezeResponsiveNavLink>
                            <BreezeResponsiveNavLink
                                :href="route('dashboard.main')"
                                :active="route().current('dashboard.main')"
                            >
                                {{ __('Dashboard') }}
                            </BreezeResponsiveNavLink>
                        </div>
                        <div class="pt-3 mt-3 mb-1 border-t border-gray-200">
                            <BreezeResponsiveNavLink
                                :href="route('logout')"
                                class="w-full text-left bg-red-50"
                                method="post"
                                as="button"
                            >
                                {{ __('Logout') }}
                            </BreezeResponsiveNavLink>
                        </div>
                    </div>
                    <div class="pt-3 mt-3 mb-1 border-t border-gray-200" v-else>
                        <Button
                            @click="showLoginModal = true"
                            padding="md"
                            class="w-full mb-2 justify-center"
                        >
                            {{ __('Log in') }}
                        </Button>
                    </div>
                </div>
            </nav>

            <div
                v-if="showLoginModal"
                class="fixed flex justify-center items-center inset-0 bg-gray-900 bg-opacity-20 z-50 bg-login"
                @click="closeModal($event)"
            >
                <div class="bg-white rounded-md shadow-md">
                    <form @submit.prevent="login()" class="px-3 pt-2 border-b mb-2 pb-2">
                        <div class="grid grid-cols-1 gap-3">
                            <InputLabel
                                id="email"
                                type="email"
                                :label="__('Email')"
                                autocomplete="email"
                                v-model="login_form.email"
                            />
                            <InputLabel
                                id="password"
                                type="password"
                                :label="__('Password')"
                                autocomplete="current-password"
                                v-model="login_form.password"
                            />
                        </div>

                        <div class="block mt-4">
                            <label class="flex items-center">
                                <breeze-checkbox name="remember" v-model:checked="login_form.remember" />
                                <span class="ml-2 text-sm text-gray-600">{{__('Remember me')}}</span>
                            </label>
                        </div>

                        <div class="mt-4">
                                <Button
                                    :class="{
                                        'opacity-25': login_form.processing
                                    }"
                                    :disabled="login_form.processing"
                                    padding="md"
                                    class="w-full mb-2 justify-center"
                                >
                                    {{ __('Log in') }}
                                </Button>

                                <InertiaLink
                                    :href="route('password.request')"
                                    class="underline text-sm text-gray-600 hover:text-gray-900">
                                    {{__('Forgot your password?')}}
                                </InertiaLink>
                        </div>
                    </form>
                    <BreezeDropdownLink :href="route('register')">
                        {{ __('Register') }}
                    </BreezeDropdownLink>
                </div>
            </div>

            <Flash
                v-if="$page.props.flash.error || $page.props.flash.success"
                :locale="lang"

                @leave="ev => {
                    $page.props.flash.error = ev;
                    $page.props.flash.success = ev;
                }"
            />
            <!-- Page Heading -->
            <header class="bg-white shadow" v-if="$slots.header">
                <div class="max-w-screen-xl mx-auto py-5 sm:px-6 px-3">
                    <slot name="header" />
                </div>
            </header>

            <!-- Page Content -->
            <main class="py-10 md:px-10 sm:px-6 px-3">
                <slot />
            </main>

            <footer class="w-full mt-auto">
                <div class="bg-gray-900 w-full"
                    :class="{
                        ['mt-16'] : $slots.footerAddon || ( $page.props.lottery && footerContainer ),
                    }"
                >
                <div class="max-w-screen-2xl mx-auto md:px-10 sm:px-6 px-3">

                    <div v-if="$slots.footerAddon"
                        class="relative md:w-3/4 mx-auto -top-16 bg-white rounded-md shadow-md lg:px-8 sm:px-4 px-2 py-4">
                        <slot name="footerAddon"/>
                    </div>
                    <div
                        v-else-if="$page.props.lottery && $page.props.lottery.value && footerContainer"
                        class="relative md:w-3/4 mx-auto -top-16 bg-white rounded-md shadow-md lg:px-8 sm:px-4 px-2 flex items-center cursor-pointer"
                        :style="[
                            $page.props.lottery.value.bg_color ? 'background-color:' + $page.props.lottery.value.bg_color : ''
                        ]"
                        @click="gotoLottery()"
                    >
                        <div class="grid grid-cols-4 w-full">
                            <div class="lg:col-span-2 col-span-4 ql-snow">
                                <div class="ql-editor content sm:-mx-4 -mx-2" v-html="$page.props.lottery.value.content"></div>
                            </div>
                            <div class="lg:col-span-2 col-span-4 lg:pb-0 pb-4 flex flex-col items-center justify-center">
                                <!-- <InertiaLink
                                    :href="route('lottery')"
                                    as="button"
                                    class="flex font-bold bg-gray-100 px-4 items-center justify-center py-1 w-1/2 m-2 rounded-md hover:shadow-md hover:bg-gray-200 transition duration-300">
                                    {{__('Go to lottery')}}
                                    <SvgIcon
                                        name="arrow-long-right"
                                    />
                                </InertiaLink> -->
                                <img class="mx-auto mt-autos" v-if="$page.props.lottery.value.image" :src="$page.props.lottery.value.image" />
                            </div>
                        </div>
                    </div>

                    <div class="text-gray-200 text-sm pt-5"
                        :class="[
                            $page.props.lottery && $page.props.lottery.value && footerContainer ? '-mt-16' : ''
                        ]"
                    >
                        <div class="grid sm:grid-cols-4 grid-cols-1 gap-3">
                            <div class="grid md:grid-cols-2 gap-3 sm:col-span-2">
                                <div>
                                    <div class="flex md:justify-start justify-center ml-5">
                                        <InertiaLink :href="route('home')" class="flex items-center">
                                            <BreezeApplicationLogo type="white" class="block h-12 w-auto mr-2" />
                                        </InertiaLink>
                                    </div>
                                    <ul>
                                        <li class="flex items-center md:justify-start justify-center mt-2">
                                            <SvgIcon class="mr-1" :size="18" name="geo"/>
                                            <div class="address">
                                                <p>Forte-Studio Łukasz Maj</p>
                                                <p v-html="$page.props.siteData.address.replace(/\n/g, '<br />')"></p>
                                            </div>
                                        </li>
                                        <li class="flex justify-start md:justify-start justify-center my-2">
                                            <a class="flex items-center hover:underline" :href="'mailto:' + $page.props.siteData.email">
                                                <SvgIcon class="mr-1" :size="18" name="email"/>
                                                {{$page.props.siteData.email}}
                                            </a>

                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <ul class="space-x-3 mb-3 flex items-center md:justify-start justify-center">
                                        <li class="cursor-pointer bg-gray-200 text-gray-900 hover:bg-red-700 hover:text-gray-200 p-2 rounded-full transition duration-300">
                                            <a
                                                target="_blank"
                                                :href="$page.props.social.facebook ? $page.props.social.facebook : '#'"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="24" height="24" viewBox="0 0 24 24">
                                                    <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/>
                                                </svg>
                                            </a>
                                        </li>
                                        <li class="cursor-pointer bg-gray-200 text-gray-900 hover:bg-red-700 hover:text-gray-200 p-2 rounded-full transition duration-300">
                                            <a
                                                target="_blank"
                                                :href="$page.props.social.youtube ? $page.props.social.youtube : '#'"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="24" height="24" viewBox="0 0 24 24"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/></svg>
                                            </a>
                                        </li>
                                        <li class="cursor-pointer bg-gray-200 text-gray-900 hover:bg-red-700 hover:text-gray-200 p-2 rounded-full transition duration-300">
                                            <a
                                                target="_blank"
                                                :href="$page.props.social.pinterest ? $page.props.social.pinterest : '#'"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="24" height="24" viewBox="0 0 24 24"><path d="M12 0c-6.627 0-12 5.372-12 12 0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146 1.124.347 2.317.535 3.554.535 6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z" fill-rule="evenodd" clip-rule="evenodd"/></svg>
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div class="sm:col-span-2">
                                <div class="grid sm:grid-cols-2 gap-3">
                                    <div class="md:text-left text-center">
                                        <h1 class="uppercase text-xl font-bold">{{__("Useful links")}}</h1>
                                        <ul class="space-y-2">
                                            <li>
                                                <InertiaLink :href="route('lottery')">{{__('Lottery')}}</InertiaLink>
                                            </li>
                                            <li>
                                                <InertiaLink :href="route('gallery')">{{__('Gallery')}}</InertiaLink>
                                            </li>
                                            <li>
                                                <InertiaLink :href="route('page.show', 'about-us')">{{__('About us')}}</InertiaLink>
                                            </li>
                                            <li>
                                                <InertiaLink :href="route('contact')">{{__('Contact us')}}</InertiaLink>
                                            </li>
                                        </ul>
                                    </div>
                                    <div class="sm:mt-0 mt-3 md:text-left text-center">
                                        <h1 class="uppercase text-xl font-bold">{{__("Legal")}}</h1>
                                        <ul class="space-y-2">
                                            <li><InertiaLink :href="route('page.show', 'privacy-policy')">{{__('Privacy Policy')}}</InertiaLink></li>
                                            <li><InertiaLink :href="route('page.show', 'terms-of-use')">{{__('Terms of use')}}</InertiaLink></li>
                                            <li><InertiaLink :href="route('page.show', 'contest-terms-of-use')">{{__('Lottery terms of use')}}</InertiaLink></li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr class="border-t-2 border-red-700 my-3">
                    <div class="pb-3 text-gray-200 text-xs flex justify-center">
                        <div>Copyright &copy; {{ getYear() }}&nbsp; | &nbsp;Created by <a target="_blank" class="hover:underline hover:text-gray-400" href="https://webfalcon.pl/">webfalcon.pl</a></div>
                    </div>
                </div>
                </div>
                <Cookies
                    v-if="showCookie == true"
                    @update="ev => showCookie = ev"
                />
            </footer>
        </div>
    </div>
</template>

<script>
    import Button from 'UI/Button'
    import BreezeCheckbox from 'COMP/Checkbox'
    import BreezeApplicationLogo from 'COMP/ApplicationLogo'
    import Flash from 'COMP/Flash';
    import BreezeDropdown from 'COMP/Dropdown/Dropdown'
    import BreezeDropdownLink from 'COMP/Dropdown/DropdownLink'
    import BreezeNavLink from 'COMP/NavLink'
    import BreezeResponsiveNavLink from 'COMP/ResponsiveNavLink';
    import SvgIcon from 'UI/SvgIcon';
    import IconLink from 'UI/IconLink';
    import Login from '@/Pages/Auth/Login';

    import InputLabel from 'UI/InputLabel';
    import Cookies from 'UI/Cookies';

    import { computed, ref } from 'vue'
    import { useForm, usePage } from '@inertiajs/inertia-vue3'

    import {
        getLangImage,
        getFullName,
        getYear,
    } from 'helper';

    import { Inertia } from '@inertiajs/inertia';
    import moment from 'moment';

    export default {
        components: {
            Button, BreezeCheckbox,
            BreezeApplicationLogo,

            BreezeDropdown,
            BreezeDropdownLink,
            BreezeNavLink,
            BreezeResponsiveNavLink,
            SvgIcon,
            IconLink,
            Login,
            Flash,
            InputLabel,

            Cookies,
        },

        props: {
            title: String,
            metaDescription: String,
            canResetPassword: Boolean,
            status: String,
            footerContainer: {
                type: Boolean,
                default: true,
            },
            lang: {
                type: String,
                default: null,
            }
        },
        setup(props, data) {
            const user = computed(() => usePage().props.value.auth.user);
            const guest = computed(() => usePage().props.value.auth.guest);
            const languages = computed(() => usePage().props.value.languages);
            const locale = computed(() => usePage().props.value.locale);

            const showLoginModal = ref(false);
            const showingNavigationDropdown = ref(false);

            moment.locale(locale.value);

            const login_form = useForm({
                email: '',
                password: '',
                remember: true
            });

            const testFunc = () => {
                Inertia.post("/test")
            }
            const login = () => {
                login_form.post( route('login'), {
                    preserveScroll: true,
                    onSuccess: () => {
                        showLoginModal.value = false;

                    }
                });
            }

            const hasErrors = computed(() => {
                return Object.keys(usePage().props.value.errors).length > 0;
            });

            const showCookie = ref( checkCookie(usePage().props.value.appName + '_cookie_consent') != 'on' ? true : false);
            function checkCookie(name) {
                var nameEQ = name + "=";
                var ca = document.cookie.split(';');
                for(var i=0;i < ca.length;i++) {
                    var c = ca[i];
                    while (c.charAt(0)==' ') c = c.substring(1,c.length);
                    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
                }
                return null;
            }

            return {
                user,
                guest,
                languages,
                locale,
                testFunc,
                login_form,
                login,
                showLoginModal,
                showingNavigationDropdown,

                hasErrors,
                getFullName,
                getYear,

                checkCookie,
                showCookie
            }
        },
        methods: {
            getLangImage,

            closeModal(ev) {
                if($(ev.target).hasClass('bg-login')) this.showLoginModal = false;
            },
            gotoLottery(){
                Inertia.visit( route('lottery') );
            }
        },
        watch: {
            locale(val){
                moment.locale(val);
                // this.$lang().locale = val;

            },

            '$page.props.errors'(val) {
                if(Object.keys(val).length > 0) usePage().props.value.flash.error = 'The given data was invalid';
            }
        },
    }
</script>
